import React, { useState, useEffect, ReactNode } from 'react';
import { X, type LucideIcon } from 'lucide-react';
import { ContextMenu } from '../../molecules/ContextMenu';
import './LensePanel.css';

/**
 * Management action attached to a row (rename/delete/...).
 * Rendered inside the row's three-dots ContextMenu.
 */
export interface LenseItemAction {
  /** Unique action ID */
  id: string;
  /** Display label */
  label: string;
  /** Optional Lucide icon component */
  icon?: LucideIcon;
  /** Whether this is a danger/destructive action (shows red) */
  danger?: boolean;
  /** Invoked when the action is chosen from the menu */
  onSelect: () => void;
}

export interface LenseItem {
  /** Unique item ID */
  id: string;
  /** Display name */
  name: string;
  /** Lucide icon component (for top-level items) */
  icon?: ReactNode;
  /** Item count badge */
  count?: number;
  /** Nested sub-items */
  children?: LenseSubItem[];
  /** Management actions shown in a three-dots menu on the row (optional) */
  actions?: LenseItemAction[];
}

export interface LenseSubItem {
  /** Unique sub-item ID */
  id: string;
  /** Display name */
  name: string;
  /** Item count badge */
  count?: number;
  /**
   * Small right-aligned secondary text (e.g. relative recency like "2 Std.").
   * Rendered only when no count is shown; count takes precedence.
   */
  meta?: string;
  /** Management actions shown in a three-dots menu on the row (optional) */
  actions?: LenseItemAction[];
}

export interface LensePanelProps {
  /** Items to display in the lense panel */
  items: LenseItem[];
  /** Currently selected item ID */
  selectedItem?: string;
  /** Currently selected sub-item ID */
  selectedSubItem?: string | null;
  /** Selection handler */
  onSelect?: (itemId: string, subItemId: string | null) => void;
  /** Panel title (optional) */
  title?: string;
  /** Whether mobile drawer is open */
  isMobileOpen?: boolean;
  /** Close mobile drawer handler */
  onMobileClose?: () => void;
  /** Optional class name */
  className?: string;
  /** Optional slot rendered between the title and the nav (e.g. "new" buttons) */
  headerContent?: ReactNode;
  /** Aria label for the per-row actions trigger (defaults to "Aktionen") */
  actionsAriaLabel?: string;
}

interface LenseNavItemProps {
  item: LenseItem | LenseSubItem;
  isSelected: boolean;
  isExpanded?: boolean;
  onSelect: (itemId: string, subItemId: string | null) => void;
  level?: number;
  selectedItem: string;
  selectedSubItem: string | null;
  parentItemId?: string;
  actionsAriaLabel: string;
}

function LenseNavItem({
  item,
  isSelected,
  isExpanded = false,
  onSelect,
  level = 0,
  selectedItem,
  selectedSubItem,
  parentItemId,
  actionsAriaLabel,
}: LenseNavItemProps) {
  const isSubItem = level === 1;
  const hasChildren = 'children' in item && item.children && item.children.length > 0;
  const hasActions = item.actions !== undefined && item.actions.length > 0;

  // For top-level items: selected when it's the active item and no sub-item is selected
  // For sub-items: selected when this specific sub-item is active
  const isActiveItem = isSubItem
    ? isSelected
    : selectedItem === item.id && !selectedSubItem;

  const showsCount = 'count' in item && item.count !== undefined && item.count > 0;

  const handleClick = () => {
    if (isSubItem && parentItemId) {
      onSelect(parentItemId, item.id);
    } else {
      onSelect(item.id, null);
    }
  };

  const navButton = (
    <button
      className={`lense-panel__nav-button ${isSubItem ? 'lense-panel__nav-button--sub' : ''} ${
        isActiveItem ? 'lense-panel__nav-button--active' : ''
      }`}
      onClick={handleClick}
      aria-current={isActiveItem ? 'page' : undefined}
    >
      {!isSubItem && 'icon' in item && item.icon && (
        <span className="lense-panel__nav-icon">{item.icon}</span>
      )}
      <span className="lense-panel__nav-name">{item.name}</span>
      {showsCount && (
        <span className={`lense-panel__nav-count${isSubItem ? ' lense-panel__nav-count--sub' : ''}`}>{item.count}</span>
      )}
      {!showsCount && 'meta' in item && item.meta && (
        <span className="lense-panel__nav-meta">{item.meta}</span>
      )}
    </button>
  );

  return (
    <div className="lense-panel__nav-item">
      {hasActions ? (
        <div
          className={`lense-panel__row lense-panel__row--with-actions${
            isActiveItem ? ' lense-panel__row--active' : ''
          }`}
        >
          {navButton}
          {/* stopPropagation: interacting with the menu must never select the row */}
          <div className="lense-panel__row-actions" onClick={(e) => e.stopPropagation()}>
            <ContextMenu
              items={item.actions!.map((action) => ({
                id: action.id,
                label: action.label,
                icon: action.icon,
                danger: action.danger,
                onClick: action.onSelect,
              }))}
              position="bottom-right"
              ariaLabel={actionsAriaLabel}
            />
          </div>
        </div>
      ) : (
        navButton
      )}

      {/* Children / Sub-items */}
      {hasChildren && isExpanded && !isSubItem && 'children' in item && (
        <div className="lense-panel__nav-children">
          {item.children!.map((child) => (
            <LenseNavItem
              key={child.id}
              item={child}
              isSelected={selectedSubItem === child.id}
              level={1}
              onSelect={onSelect}
              selectedItem={selectedItem}
              selectedSubItem={selectedSubItem}
              parentItemId={item.id}
              actionsAriaLabel={actionsAriaLabel}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * LensePanel - A reusable category/filter lense panel.
 * Used across all app sections (Workspace, My Farm, Documents, History).
 */
export const LensePanel: React.FC<LensePanelProps> = ({
  items,
  selectedItem = 'all',
  selectedSubItem = null,
  onSelect,
  title,
  isMobileOpen = false,
  onMobileClose,
  className = '',
  headerContent,
  actionsAriaLabel = 'Aktionen',
}) => {
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  // Auto-expand the selected item's parent when a sub-item is selected
  useEffect(() => {
    if (selectedItem && selectedItem !== 'all') {
      setExpandedItemId(selectedItem);
    } else {
      setExpandedItemId(null);
    }
  }, [selectedItem]);

  const handleSelect = (itemId: string, subItemId: string | null) => {
    onSelect?.(itemId, subItemId);

    // Close mobile drawer when an item is selected
    if (isMobileOpen) {
      onMobileClose?.();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div className="lense-panel__overlay" onClick={onMobileClose} />
      )}

      {/* Panel */}
      <aside
        data-testid="lense-panel"
        className={`lense-panel ${isMobileOpen ? 'lense-panel--mobile-open' : ''} ${className}`}
      >
        <div className="lense-panel__inner">
          {/* Optional title */}
          {title && <h2 className="lense-panel__title">{title}</h2>}

          {/* Optional header slot (e.g. new-conversation / new-project buttons) */}
          {headerContent && (
            <div className="lense-panel__header-content">{headerContent}</div>
          )}

          {/* Navigation */}
          <nav className="lense-panel__nav">
            {items.map((item) => (
              <LenseNavItem
                key={item.id}
                item={item}
                isSelected={selectedItem === item.id && !selectedSubItem}
                isExpanded={expandedItemId === item.id}
                onSelect={handleSelect}
                selectedItem={selectedItem}
                selectedSubItem={selectedSubItem}
                actionsAriaLabel={actionsAriaLabel}
              />
            ))}
          </nav>

          {/* Mobile close button */}
          <button className="lense-panel__close-mobile" onClick={onMobileClose}>
            <X className="lense-panel__close-icon" />
            <span>Close Menu</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default LensePanel;
